import { useMemo, useEffect, useState, type FC } from 'react';
import { motion } from 'motion/react';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import { CustomNumberFlow } from '@/shared/components/CustomNumberFlow';
import { ReadonlyButton, ReadonlyHTMLButton } from '@/shared/components/dashboards/Readonly';
import Pill from '@/shared/components/flip-ui-kit/Pill';
import { ApyPeriodSelect } from '@/shared/components/lending/ApyPeriodSelect';
import { BorrowSliderReadonly } from '@/shared/components/lending/BorrowSlider';
import { ChainLogo } from '@/shared/components/logo/ChainLogo';
import { TokenLogo } from '@/shared/components/logo/TokenLogo';
import { TokenWithChain } from '@/shared/components/logo/TokenWithChain';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { isLiquidityProvisionApp } from '@/shared/featureFlags';
import { useLendingConfig } from '@/shared/hooks/useLendingConfig';
import { useLendingPools } from '@/shared/hooks/useLendingPools';
import { useLoanRiskType } from '@/shared/hooks/useLoanRiskType';
import { type Loan, type LpLoanAccount } from '@/shared/hooks/useLpLoanAccount';
import { useLpLoanFees } from '@/shared/hooks/useLpLoanFees';
import usePrevious from '@/shared/hooks/usePrevious';
import MinusCircleIcon from '@/shared/icons/flip-ui-kit/large/MinusCircle';
import PlusCircleIcon from '@/shared/icons/flip-ui-kit/large/PlusCircle';
import ArrowIcon from '@/shared/icons/flip-ui-kit/small/Arrow';
import BorrowIcon from '@/shared/icons/flip-ui-kit/small/Borrow';
import Clock from '@/shared/icons/flip-ui-kit/small/Clock';
import { apyToText } from '@/shared/utils/boost';
import { chainLabel } from '@/shared/utils/chainflip';
import { defaultAnimationProps } from '@/shared/utils/consts';
import { chainflipAssetMap } from '@/shared/utils/env';
import {
  type BorrowApyPeriodOption,
  BORROW_APY_PERIODS,
  DEFAULT_APY_PERIOD,
} from '@/shared/utils/lending';
import { formatUsdValue } from '@/shared/utils/numbers';
import { formatBps } from '@/shared/utils/strings';
import { Divider } from '../Divider';
import Button, { type ButtonProps } from '../flip-ui-kit/Button';

const LoansTable = ({
  loans,
  loanFees,
  isLiquidating,
  openRepayModal,
}: {
  loans: Loan[];
  loanFees?: Record<string, number>;
  isLiquidating: boolean;
  openRepayModal?: (loan: Loan) => void;
}) => {
  const { lendingPools, isLoading: isPoolsLoading } = useLendingPools();
  const [borrowApyPeriod, setBorrowApyPeriod] = useState<BorrowApyPeriodOption>(DEFAULT_APY_PERIOD);

  return (
    <TableV2
      columns={[
        'Asset Borrowed',
        'Debt Amount',
        {
          tooltip:
            borrowApyPeriod.value === 'CURRENT'
              ? 'The annualized borrowing rate this market is charging right now, based on its current utilisation.'
              : `The average annualized borrowing rate across the market over the past ${borrowApyPeriod.description}.`,
          name: 'Borrow Rate',
          icon: (
            <ApyPeriodSelect
              periods={BORROW_APY_PERIODS}
              selected={borrowApyPeriod}
              onChange={setBorrowApyPeriod}
            />
          ),
        },
        ...(openRepayModal
          ? [
              {
                name: '',
                alignment: 'right' as const,
              },
            ]
          : []),
      ]}
      thClassName="border-0 px-0"
      scrollClassName="max-h-[400px] overflow-y-scroll !opacity-100"
      tbodyClassName="[&>tr>td:first-child]:pl-0 [&>tr>td]:px-0"
      className="border-0"
      rows={loans.map((loan) => {
        const { id, asset, principalAmount, principalAmountUsd } = loan;
        const pool = lendingPools.find((p) => p.asset === asset);
        const borrowRate =
          pool && (borrowApyPeriod.value === 'CURRENT' || pool.hasProjectedApy)
            ? pool.projectedBorrowApyByPeriod[borrowApyPeriod.value]
            : undefined;
        return (
          <tr key={id}>
            <td>
              <div className="flex flex-col">
                <div className="flex items-center gap-x-2 font-aeonikMedium text-cf-white">
                  <div className="relative h-[37px] w-[35px]">
                    <div className="absolute bottom-px">
                      <TokenWithChain
                        chainLogo={<ChainLogo chain={chainflipAssetMap[asset].chain} />}
                        tokenLogo={<TokenLogo size="large" token={asset} />}
                        size="large"
                      />
                    </div>
                  </div>
                  <div className="font-aeonikMedium">
                    <span className="text-white">{chainflipAssetMap[asset].symbol}</span>
                    <div className="text-cf-light-2">
                      on {chainLabel(chainflipAssetMap[asset].chain.chainflipId)}
                    </div>
                  </div>
                </div>
              </div>
            </td>
            <td>
              <div className="flex flex-col font-aeonikMedium">
                <span className="text-cf-white">
                  {principalAmount.toFormatted()} {chainflipAssetMap[asset].symbol}{' '}
                </span>
                <span className="text-cf-light-2">{formatUsdValue(principalAmountUsd)}</span>
              </div>
            </td>
            <td>
              <div className="flex min-w-[180px] flex-col font-aeonikMedium">
                {isPoolsLoading ? (
                  <SkeletonLine width={50} />
                ) : (
                  <span className="text-cf-white">
                    {borrowRate === undefined ? '-' : apyToText(borrowRate)}
                  </span>
                )}
                <span className="text-cf-light-2">
                  {loanFees && loanFees[id] ? formatUsdValue(loanFees[id]) : '$0.00'}
                </span>
              </div>
            </td>
            {openRepayModal && (
              <td>
                <div className="flex w-full justify-end pr-3">
                  <ReadonlyButton
                    size="small"
                    type="secondary-standard"
                    disabled={isLiquidating}
                    onClick={() => openRepayModal(loan)}
                  >
                    Repay
                  </ReadonlyButton>
                </div>
              </td>
            )}
          </tr>
        );
      })}
    />
  );
};

const CollateralBalancesTable = ({
  collateralBalances,
  openSupplyModal,
  openRemoveSupplyModal,
}: {
  collateralBalances: LpLoanAccount['collateralBalances'];
  openSupplyModal?: (token: ChainflipToken) => void;
  openRemoveSupplyModal?: (token: ChainflipToken) => void;
}) => (
  <TableV2
    columns={[
      'Asset',
      'Amount',
      ...(openSupplyModal
        ? [
            {
              name: '',
              alignment: 'right' as const,
            },
          ]
        : []),
    ]}
    thClassName="border-0 px-0"
    tbodyClassName="[&>tr>td:first-child]:pl-0 [&>tr>td]:px-0"
    className="border-0"
    rows={collateralBalances.map((collateral) => {
      const { amount, amountUsd, asset } = collateral;
      return (
        <tr key={asset}>
          <td>
            <div className="flex flex-col">
              <div className="flex items-center gap-x-2 font-aeonikMedium text-cf-white">
                <div className="relative h-[37px] w-[35px]">
                  <div className="absolute bottom-px">
                    <TokenWithChain
                      chainLogo={<ChainLogo chain={chainflipAssetMap[asset].chain} />}
                      tokenLogo={<TokenLogo size="large" token={asset} />}
                      size="large"
                    />
                  </div>
                </div>
                <div className="font-aeonikMedium">
                  <div className="text-white">{chainflipAssetMap[asset].symbol}</div>
                  <div className="text-cf-light-2">
                    on {chainLabel(chainflipAssetMap[asset].chain.chainflipId)}
                  </div>
                </div>
              </div>
            </div>
          </td>
          <td>
            <div className="flex flex-col font-aeonikMedium">
              <span className="text-cf-white">
                {amount.toFormatted()} {chainflipAssetMap[asset].symbol}{' '}
              </span>
              <span className="text-cf-light-2">{formatUsdValue(amountUsd)}</span>
            </div>
          </td>
          {openRemoveSupplyModal && openSupplyModal && (
            <td className="text-right">
              <div className="flex justify-end gap-x-2">
                <ReadonlyButton
                  size="small"
                  type="secondary-standard"
                  className="!min-w-[30px]"
                  aria-label="remove-supply"
                  onClick={() => {
                    openRemoveSupplyModal(chainflipAssetMap[asset]);
                  }}
                >
                  <MinusCircleIcon className="text-cf-red-2" />
                </ReadonlyButton>
                <ReadonlyButton
                  size="small"
                  type="secondary-standard"
                  className="!min-w-[30px]"
                  aria-label="add-supply"
                  onClick={() => {
                    openSupplyModal(chainflipAssetMap[asset]);
                  }}
                >
                  <PlusCircleIcon className="text-cf-green-3" />
                </ReadonlyButton>
              </div>
            </td>
          )}
        </tr>
      );
    })}
  />
);

const LiquidationBanner = ({
  type,
  softLiquidationLtvThresholdBps,
}: {
  type: 'liquidating' | 'warning' | 'stopped' | 'voluntary-liquidating';
  softLiquidationLtvThresholdBps?: number;
}) => {
  const imgAlt = 'liquidation banner';
  let imgSrc = '';
  let title: React.ReactNode = null;
  let message: React.ReactNode = null;

  if (type === 'liquidating') {
    imgSrc = '/illustration-liquidation.png';
    title = <span className="text-20 text-white">Liquidation in Progress</span>;
    message = (
      <span>
        Your LTV exceeded the{' '}
        <span className="text-white">{formatBps(softLiquidationLtvThresholdBps!)}</span> liquidation
        threshold. The protocol is now selling part of your collateral until your LTV returns to a
        healthy level
      </span>
    );
  } else if (type === 'warning') {
    imgSrc = '/illustration-liquidation.png';
    title = <span className="text-20 text-white">High LTV Warning</span>;
    message = (
      <span>
        Your LTV is nearing the{' '}
        <span className="text-white">{formatBps(softLiquidationLtvThresholdBps!)}</span> liquidation
        threshold. <span className="text-white">Add collateral or repay </span>
        to avoid liquidation
      </span>
    );
  } else if (type === 'stopped') {
    imgSrc = '/illustration-stopped-liquidation.png';
    title = <span className="text-20 text-white">Liquidation Stopped</span>;
    message = (
      <>
        Your LTV has returned to a healthy level, so the protocol has stopped liquidating your
        collateral.
      </>
    );
  } else if (type === 'voluntary-liquidating') {
    imgSrc = '/illustration-liquidation.png';
    title = (
      <div className="text-20 text-cf-orange-2">
        Manual Liquidation <span className="ml-0.5 text-white">in Progress</span>
      </div>
    );
    message = (
      <span>
        The protocol will gradually sell part of your collateral until the outstanding debt is fully
        repaid. Any remaining value stays in your collateral balance.
      </span>
    );
  }

  return (
    <motion.div key={type} {...defaultAnimationProps}>
      <img src={imgSrc} alt={imgAlt} className="absolute bottom-0 right-0 h-full rounded-md" />
      <div className="text-20 text-white">{title}</div>
      <div className="mt-3 min-h-[40px] max-w-[505px] text-14 text-cf-light-2">{message}</div>
    </motion.div>
  );
};

const getLiquidationBannerType = (
  isLiquidating: boolean,
  isVoluntaryLiquidating: boolean,
  showStoppedLiquidationBanner: boolean,
) => {
  if (showStoppedLiquidationBanner) return 'stopped';
  if (isLiquidating) return 'liquidating';
  if (isVoluntaryLiquidating) return 'voluntary-liquidating';
  return 'warning';
};

export const BorrowPosition = ({
  openRepayModal,
  openSupplyModal,
  openRemoveSupplyModal,
  openBorrowModal,
  openManualLiquidationModal,
  loanAccount,
  loanAccountLoading,
  SupplyButton = Button,
}: {
  openRepayModal?: (loan: Loan) => void;
  openSupplyModal?: (token: ChainflipToken) => void;
  openRemoveSupplyModal?: (token: ChainflipToken) => void;
  openBorrowModal?: (token: ChainflipToken) => void;
  openManualLiquidationModal?: () => void;
  loanAccount: LpLoanAccount;
  loanAccountLoading: boolean;
  SupplyButton?: FC<ButtonProps>;
}) => {
  const {
    targetLtvThresholdBps,
    softLiquidationLtvThresholdBps,
    isLoading: lendingConfigLoading,
  } = useLendingConfig();

  const {
    loans,
    collateralBalances,
    loanToValueBps,
    totalBorrowAmountUsd,
    borrowPowerUsedBps,
    totalCollateralBalanceUsd,
  } = loanAccount;

  const collateralValueLockedUsd = (borrowPowerUsedBps / 10_000) * totalCollateralBalanceUsd;

  const maxBorrowCapacityUsd = Math.max(
    (totalCollateralBalanceUsd * (targetLtvThresholdBps ?? 0)) / 10_000,
    0,
  );
  const loanIds = useMemo(() => loans.map((loan) => loan.id), [loans]);
  const { data: loanFeesPerLoan } = useLpLoanFees({ loanIds });

  const { riskType, riskColor, gradient, isLtvHighRisk } = useLoanRiskType(loanToValueBps);

  const isLiquidating = loans.some(
    (loan) => loan.status === 'HardLiquidation' || loan.status === 'SoftLiquidation',
  );
  const isVoluntaryLiquidating = loans.some((loan) => loan.status === 'SoftVoluntaryLiquidation');

  const prevIsLiquidating = usePrevious(isLiquidating);
  const wasLiquidating = prevIsLiquidating === true;
  const [showStoppedLiquidationBanner, setShowStoppedLiquidationBanner] = useState(false);

  useEffect(() => {
    if (isLiquidating || isVoluntaryLiquidating) {
      setShowStoppedLiquidationBanner(false);
      return undefined;
    }
    const stoppedLiquidating = wasLiquidating && !isLiquidating;

    if (stoppedLiquidating) {
      setShowStoppedLiquidationBanner(true);
      const timer = setTimeout(() => {
        setShowStoppedLiquidationBanner(false);
      }, 10_000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isLiquidating, isVoluntaryLiquidating]);

  const isLoading = lendingConfigLoading || loanAccountLoading;

  const showLiquidationBanner =
    isLiquidityProvisionApp &&
    (isLiquidating || isVoluntaryLiquidating || isLtvHighRisk || showStoppedLiquidationBanner);
  return (
    <div className="flex flex-col gap-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-y-6 rounded-md border border-cf-gray-3-5 bg-cf-gray-3 p-6 shadow-grayPop3">
          {showLiquidationBanner && !isLoading && (
            <div className="relative flex flex-col gap-y-3 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-6 font-aeonikMedium">
              <LiquidationBanner
                type={getLiquidationBannerType(
                  isLiquidating,
                  isVoluntaryLiquidating,
                  showStoppedLiquidationBanner,
                )}
                softLiquidationLtvThresholdBps={softLiquidationLtvThresholdBps}
              />
            </div>
          )}
          <div className="flex flex-col items-start gap-x-[40px] gap-y-4 md:flex-row md:items-start md:gap-y-0">
            <div className="flex flex-col gap-y-2">
              <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-light-2">
                Loan-to-Value (LTV)
                <QuestionMarkTooltip content="The ratio between your borrowed amount and the value of your supplied assets. Higher LTV increases liquidation risk." />
              </div>
              <div className="flex items-center gap-x-1 font-aeonikMedium text-16 text-cf-white">
                {/* eslint-disable-next-line no-nested-ternary */}
                {isLoading ? (
                  <SkeletonLine width={100} />
                ) : totalCollateralBalanceUsd > 0 || loans.length > 0 ? (
                  <>
                    <CustomNumberFlow value={formatBps(loanToValueBps)} />
                    {loanToValueBps > 0 &&
                      targetLtvThresholdBps > 0 &&
                      softLiquidationLtvThresholdBps > 0 && (
                        <Pill color={riskColor} text={riskType} />
                      )}
                  </>
                ) : (
                  '-'
                )}
              </div>
            </div>
            <div className="flex flex-col gap-y-2">
              <span className="font-aeonikMedium text-14 text-cf-light-2">
                Borrowed / Borrow Capacity
              </span>
              <div className="font-aeonikMedium text-16">
                {isLoading ? (
                  <SkeletonLine />
                ) : (
                  <>
                    <CustomNumberFlow
                      className="text-cf-white"
                      value={formatUsdValue(totalBorrowAmountUsd)}
                    />
                    <span className="text-cf-light-2">
                      {' '}
                      / <CustomNumberFlow value={formatUsdValue(maxBorrowCapacityUsd)} />
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-y-2">
              <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-light-2">
                Borrow Power Used
                <QuestionMarkTooltip content="The percentage of your total borrowing capacity currently in use." />
              </div>
              <div className="font-aeonikMedium text-16 text-cf-white">
                {/* eslint-disable-next-line no-nested-ternary */}
                {isLoading ? (
                  <SkeletonLine />
                ) : totalCollateralBalanceUsd > 0 || loans.length > 0 ? (
                  <CustomNumberFlow value={formatBps(borrowPowerUsedBps)} />
                ) : (
                  '-'
                )}
              </div>
            </div>
          </div>
          {isLoading ? (
            <SkeletonLine />
          ) : (
            totalCollateralBalanceUsd > 0 &&
            targetLtvThresholdBps &&
            softLiquidationLtvThresholdBps && (
              <BorrowSliderReadonly
                max={totalCollateralBalanceUsd}
                thumbValue={totalBorrowAmountUsd}
                gradient={gradient}
                bpsThresholds={[targetLtvThresholdBps, softLiquidationLtvThresholdBps]}
              />
            )
          )}
          {loans.length > 0 ? (
            <>
              <LoansTable
                loans={loans}
                loanFees={loanFeesPerLoan}
                openRepayModal={openRepayModal}
                isLiquidating={isLiquidating || isVoluntaryLiquidating}
              />
              {openManualLiquidationModal && (
                <div className="flex items-center gap-x-1 font-aeonikMedium text-14">
                  <span className="text-cf-light-3">Can&apos;t repay?</span>
                  <ReadonlyHTMLButton
                    type="button"
                    className="cursor-default pt-0.5 text-cf-green-3 hover:text-cf-green-2 disabled:text-cf-gray-5"
                    onClick={openManualLiquidationModal}
                  >
                    Start liquidation
                  </ReadonlyHTMLButton>
                </div>
              )}
            </>
          ) : (
            !isLoading && (
              <>
                <Divider className="h-px" />
                <div className="flex flex-col items-center justify-center gap-y-4 py-3 font-aeonikMedium text-12 text-cf-light-3">
                  {openBorrowModal && totalCollateralBalanceUsd ? (
                    <>
                      <div className="inline-flex items-center gap-x-1">
                        <Clock />
                        No active loans. Click Borrow to create a loan up to{' '}
                        {formatUsdValue(maxBorrowCapacityUsd, false)}.
                      </div>
                      <Button
                        size="small"
                        onClick={() => openBorrowModal(chainflipAssetMap.Usdc)}
                        icon={<BorrowIcon />}
                        iconPos="left"
                      >
                        Borrow
                      </Button>
                    </>
                  ) : (
                    <div className="flex items-center gap-x-1">
                      <Clock />
                      No active loans.
                    </div>
                  )}
                </div>
              </>
            )
          )}
        </div>
        <div className="flex flex-col gap-y-6 rounded-md border border-cf-gray-3-5 bg-cf-gray-3 p-6 shadow-grayPop3">
          <div className="flex flex-col items-start gap-x-[56px] gap-y-4 md:flex-row md:items-start md:gap-y-0">
            <div className="flex flex-col gap-y-2">
              <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-light-2">
                Collateral Value
                <QuestionMarkTooltip content="Total USD value of all assets you've supplied as collateral across all lending pools." />
              </div>
              <div className="flex items-center gap-x-1 font-aeonikMedium text-16 text-cf-white">
                {isLoading ? (
                  <SkeletonLine width={100} />
                ) : (
                  <CustomNumberFlow value={formatUsdValue(totalCollateralBalanceUsd)} />
                )}
              </div>
            </div>
            <div className="col-span-2 flex flex-col gap-y-2">
              <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-light-2">
                Collateral Value Locked
                <QuestionMarkTooltip content="The portion of your supplied collateral locked against your active loan. It cannot be withdrawn until the loan is repaid." />
              </div>
              <div className="flex items-center gap-x-1 font-aeonikMedium text-16 text-cf-white">
                {/* eslint-disable-next-line no-nested-ternary */}
                {isLoading ? (
                  <SkeletonLine width={100} />
                ) : totalCollateralBalanceUsd > 0 ? (
                  <CustomNumberFlow value={formatUsdValue(collateralValueLockedUsd)} />
                ) : (
                  '-'
                )}
              </div>
            </div>
          </div>
          {!loanAccountLoading && collateralBalances.length > 0 ? (
            <CollateralBalancesTable
              collateralBalances={collateralBalances}
              openSupplyModal={openSupplyModal}
              openRemoveSupplyModal={openRemoveSupplyModal}
            />
          ) : (
            !loanAccountLoading && (
              <>
                <Divider className="h-px" />
                <div className="flex flex-col items-center gap-y-4 py-3">
                  <div className="flex items-center gap-x-1 font-aeonikMedium text-12 text-cf-light-3">
                    <Clock />
                    {openSupplyModal
                      ? 'No active loans. Supply any asset to start borrowing.'
                      : 'No collateral supplied.'}
                  </div>
                  {openSupplyModal && (
                    <SupplyButton
                      onClick={() => openSupplyModal(chainflipAssetMap.Btc)}
                      icon={<ArrowIcon direction="up" />}
                      iconPos="left"
                      size="small"
                    >
                      Supply
                    </SupplyButton>
                  )}
                </div>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
};
